import { Box, Divider, ListItemIcon, Menu, MenuItem, ThemeProvider, Typography } from '@mui/material'
import { alpha } from '@mui/material/styles'
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined'
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined'
import SwapHorizOutlinedIcon from '@mui/icons-material/SwapHorizOutlined'
import KeyboardArrowDownOutlinedIcon from '@mui/icons-material/KeyboardArrowDownOutlined'
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined'
import { useMemo, useState, type ReactNode } from 'react'
import { enUS, plPL } from '@mui/material/locale'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { useLocale } from '@/contexts/LocaleContext'
import { supportMailto } from '@/config/contact'
import { createAffexyTheme } from '@/theme/affexyTheme'
import LanguageSwitcher from '@/components/LanguageSwitcher'
import SygnetCluster from '@/components/researcher/SygnetCluster'

/**
 * Affexy respondent chrome — a single light top bar (brand + account menu) on a
 * light canvas, with NO sidebar. Mirrors the researcher look-and-feel but keeps
 * the surface minimal: respondents only ever see one main view, so there is
 * nothing to navigate between. Scopes the Affexy MUI theme via a nested
 * ThemeProvider, just like the researcher shell.
 */
export default function RespondentShell({ children }: { children: ReactNode }) {
  const { language } = useLocale()
  const theme = useMemo(() => createAffexyTheme(language === 'pl' ? plPL : enUS), [language])
  const { t } = useTranslation('dashboard')
  const navigate = useNavigate()
  const { user, logout, switchView } = useAuth()
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null)
  const c = theme.affexy.colors

  const fullName =
    user?.first_name && user?.last_name ? `${user.first_name} ${user.last_name}` : (user?.email ?? '')
  const initials = (user?.first_name?.[0] ?? user?.email?.[0] ?? '?').toUpperCase()

  const closeMenu = () => setMenuAnchor(null)
  const handleProfile = () => {
    closeMenu()
    navigate('/profile')
  }
  const handleSwitchToResearcher = () => {
    closeMenu()
    switchView('researcher')
    navigate('/')
  }
  const handleLogout = async () => {
    closeMenu()
    await logout()
    navigate('/login')
  }

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
          bgcolor: 'background.default',
          color: 'text.primary',
          fontFamily: theme.affexy.fonts.body,
        }}
      >
        {/* Top bar — brand lockup + account menu, no navigation */}
        <Box
          component="header"
          sx={{
            height: 60,
            flex: '0 0 60px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: { xs: 2.5, md: 4 },
            gap: 2,
            bgcolor: c.white,
            borderBottom: `1px solid ${c.ink08}`,
            position: 'sticky',
            top: 0,
            zIndex: 2,
          }}
        >
          {/* Brand */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: '8px',
                overflow: 'hidden',
                bgcolor: c.navy600,
                display: 'grid',
                placeItems: 'center',
                p: '3px',
              }}
            >
              <SygnetCluster />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.875, lineHeight: 1 }}>
              <Typography
                sx={{ fontFamily: theme.affexy.fonts.heading, fontWeight: 700, fontSize: 19, letterSpacing: '-0.01em' }}
              >
                FaceWise
              </Typography>
            </Box>
          </Box>

          {/* Account */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <LanguageSwitcher />
            <Box
              component="button"
              onClick={(e: React.MouseEvent<HTMLElement>) => setMenuAnchor(e.currentTarget)}
              aria-haspopup="true"
              aria-expanded={Boolean(menuAnchor)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                pl: 0.5,
                pr: 1,
                py: 0.5,
                border: 'none',
                bgcolor: 'transparent',
                cursor: 'pointer',
                borderRadius: 34,
                transition: 'background-color 120ms',
                '&:hover': { bgcolor: c.gray50 },
              }}
            >
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: 999,
                  background: theme.affexy.gradients.coral,
                  color: '#fff',
                  display: 'grid',
                  placeItems: 'center',
                  fontFamily: theme.affexy.fonts.heading,
                  fontWeight: 700,
                  fontSize: 12,
                  flexShrink: 0,
                }}
              >
                {initials}
              </Box>
              <Box sx={{ display: { xs: 'none', sm: 'flex' }, flexDirection: 'column', minWidth: 0, textAlign: 'left' }}>
                <Typography
                  sx={{ fontSize: 13, lineHeight: 1.2, color: c.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 160 }}
                >
                  {fullName}
                </Typography>
                <Typography sx={{ fontSize: 11, color: c.ink48 }}>{t('nav.roleRespondent')}</Typography>
              </Box>
              <KeyboardArrowDownOutlinedIcon sx={{ fontSize: 18, color: c.ink48 }} />
            </Box>

            <Menu
              anchorEl={menuAnchor}
              open={Boolean(menuAnchor)}
              onClose={closeMenu}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              slotProps={{ paper: { sx: { minWidth: 200, borderRadius: '12px', mt: 0.5 } } }}
            >
              <MenuItem onClick={handleProfile}>
                <ListItemIcon>
                  <PersonOutlineOutlinedIcon fontSize="small" />
                </ListItemIcon>
                {t('nav.profile')}
              </MenuItem>
              <MenuItem onClick={handleSwitchToResearcher}>
                <ListItemIcon>
                  <SwapHorizOutlinedIcon fontSize="small" />
                </ListItemIcon>
                {t('nav.switchToResearcher')}
              </MenuItem>
              <MenuItem
                component="a"
                href={supportMailto(t('nav.helpMailSubject'))}
                onClick={closeMenu}
              >
                <ListItemIcon>
                  <HelpOutlineOutlinedIcon fontSize="small" />
                </ListItemIcon>
                {t('nav.help')}
              </MenuItem>
              <Divider />
              <MenuItem onClick={handleLogout}>
                <ListItemIcon>
                  <LogoutOutlinedIcon fontSize="small" />
                </ListItemIcon>
                {t('nav.logout')}
              </MenuItem>
            </Menu>
          </Box>
        </Box>

        {/* Scroll area */}
        <Box sx={{ flex: 1, overflow: 'auto', p: { xs: '20px', md: '28px 32px 40px' }, bgcolor: alpha(c.gray50, 1) }}>
          {children}
        </Box>
      </Box>
    </ThemeProvider>
  )
}
